/**
 * Latency-budget monitor — per-peer, stateful.
 *
 * The pure verdict lives in ./latencyBudgetContracts; this class adds the per-peer sliding
 * window, the breach EDGE (one event per breach episode, re-armed only after a recovered
 * episode), the worst-latency reading for the bug event, and the FLOOR edge: one event per
 * stretch during which the link's own floor sits over the budget. It holds no host or
 * transport references — the host feeds it samples and acts on the events.
 *
 * The two events are not symmetric. A breach is fixable excess and the host acts on it. The
 * floor event carries a KIND. A `route` floor is a fact about the route — the guest is far
 * away — and the host only records it: nothing the encoder does can lower a floor, and a
 * distant link is not a faulty one. A `standing-buffer` floor is NOT that: the floor is a
 * window minimum, and frames standing in the receive-side buffer for the whole window are
 * absorbed into it (Android, 2026-09-19: a 320 ms floor on a 5 ms LAN was filed as "route,
 * observability only" for 45 s). The kind comes from the standing-latency detector via the
 * sample; a floor whose kind becomes `standing-buffer` is reported AGAIN even mid-stretch, so
 * it is never left suppressed under an earlier route filing. The drain acts on it (Phase 5).
 *
 * Fail direction: measured fixable excess is a breach (fail-closed); no measured data, or a
 * reading with no trusted floor to attribute it, is 'within' (absence is not a violation).
 */

import {
  LATENCY_BUDGET_GLASS_TO_GLASS_MS,
  LATENCY_BUDGET_MIN_FIXABLE_EXCESS_MS,
  LATENCY_BUDGET_VIOLATION_HOLD_MS,
  LATENCY_BUDGET_WINDOW_MS,
  classifyLatencyBudget,
  evaluateLatencyBudget,
  type LatencyBudgetClass,
  type LatencyBudgetSample,
  type LatencyBudgetVerdict,
} from './latencyBudgetContracts';
import type { LatencyFloorKind } from './standingLatency/standingLatencyContracts';

export type LatencyBreachEvent = { peerId: string; atMs: number; worstLatencyMs: number };

/**
 * The link's floor alone exceeds the budget: reported once per stretch and once per change
 * of kind to or from `standing-buffer`. `route` is observability only; `standing-buffer` is
 * acted on; `unknown` means the detector had no verdict.
 */
export type LatencyFloorEvent = { peerId: string; atMs: number; floorMs: number; kind: LatencyFloorKind };

type PeerLatency = {
  samples: LatencyBudgetSample[];
  verdict: LatencyBudgetVerdict;
  /** What the latest measured reading said. */
  latestClass: LatencyBudgetClass;
  /** True while a breach event for the current episode has already been emitted. */
  breachEmitted: boolean;
  /** Worst measured latency in the current window (for the bug event). */
  worstLatencyMs: number;
  /** The floor the latest trusted reading carried, or null. */
  floorMs: number | null;
  /** True while a floor event for the current over-budget stretch has been emitted. */
  floorEmitted: boolean;
  /** What the floor is made of, per the last sample that carried a verdict. */
  floorKind: LatencyFloorKind;
};

export class LatencyBudgetMonitor {
  private readonly budgetMs: number;
  private readonly holdMs: number;
  private readonly windowMs: number;
  private readonly minFixableExcessMs: number;
  private readonly nowMs: () => number;
  private readonly peers = new Map<string, PeerLatency>();

  constructor(opts?: {
    budgetMs?: number;
    holdMs?: number;
    windowMs?: number;
    minFixableExcessMs?: number;
    nowMs?: () => number;
  }) {
    this.budgetMs = opts?.budgetMs ?? LATENCY_BUDGET_GLASS_TO_GLASS_MS;
    this.holdMs = opts?.holdMs ?? LATENCY_BUDGET_VIOLATION_HOLD_MS;
    this.windowMs = opts?.windowMs ?? LATENCY_BUDGET_WINDOW_MS;
    this.minFixableExcessMs = opts?.minFixableExcessMs ?? LATENCY_BUDGET_MIN_FIXABLE_EXCESS_MS;
    this.nowMs = opts?.nowMs ?? (() => Date.now());
  }

  /** Feed one calibrated reading with its floor. Undefined fields are "not measured". */
  observe(peerId: string, sample: LatencyBudgetSample): LatencyBudgetVerdict {
    const peer = this.peerFor(peerId);
    peer.samples.push(sample);
    const cutoff = this.nowMs() - this.windowMs;
    peer.samples = peer.samples.filter((s) => s.atMs >= cutoff);

    const klass = classifyLatencyBudget(sample, this.budgetMs, this.minFixableExcessMs);
    if (klass !== 'unmeasured') peer.latestClass = klass;

    const verdict = evaluateLatencyBudget(
      peer.samples,
      this.budgetMs,
      this.holdMs,
      this.minFixableExcessMs,
    );
    peer.verdict = verdict;
    if (sample.calibratedMs !== undefined && sample.calibratedMs > peer.worstLatencyMs) {
      peer.worstLatencyMs = sample.calibratedMs;
    }

    if (verdict === 'within') {
      // A recovered episode re-arms the breach edge and resets the worst reading.
      peer.breachEmitted = false;
      peer.worstLatencyMs = sample.calibratedMs ?? 0;
    }

    // The floor edge follows the trusted floor alone: a floor back within budget (a route
    // change) re-arms it so the next over-budget stretch is reported again.
    if (sample.floorMs !== undefined && Number.isFinite(sample.floorMs)) {
      peer.floorMs = sample.floorMs;
      if (sample.floorMs <= this.budgetMs) peer.floorEmitted = false;
    }
    // A change of kind to or from standing-buffer is its own edge: a floor already filed as
    // route (observability only) must be reported again once it is diagnosed as a standing
    // buffer, or it stays suppressed under the wrong filing.
    if (sample.standingLatencyKind !== undefined && sample.standingLatencyKind !== peer.floorKind) {
      const wasStanding = peer.floorKind === 'standing-buffer';
      const isStanding = sample.standingLatencyKind === 'standing-buffer';
      if (wasStanding !== isStanding) peer.floorEmitted = false;
      peer.floorKind = sample.standingLatencyKind;
    }
    return verdict;
  }

  /** What the peer's floor is made of ('unknown' when never told). */
  floorKindFor(peerId: string): LatencyFloorKind {
    return this.peers.get(peerId)?.floorKind ?? 'unknown';
  }

  /** Current verdict for a peer ('within' when never sampled). */
  verdictFor(peerId: string): LatencyBudgetVerdict {
    return this.peers.get(peerId)?.verdict ?? 'within';
  }

  /** What the latest measured reading said ('unmeasured' when never sampled). */
  classFor(peerId: string): LatencyBudgetClass {
    return this.peers.get(peerId)?.latestClass ?? 'unmeasured';
  }

  /**
   * Returns a breach event exactly once per breach episode (edge, not level), or null. A
   * recovered (within) episode re-arms it so the next breach emits again.
   */
  pollBreachEvent(peerId: string): LatencyBreachEvent | null {
    const peer = this.peers.get(peerId);
    if (!peer || peer.verdict !== 'breach' || peer.breachEmitted) return null;
    peer.breachEmitted = true;
    return { peerId, atMs: this.nowMs(), worstLatencyMs: peer.worstLatencyMs };
  }

  /**
   * Returns a floor event exactly once per stretch during which the link's trusted floor
   * exceeds the budget (and once more when its kind changes to or from `standing-buffer`),
   * or null. The event's `kind` says whether it is observability only (`route`) or a
   * standing buffer to be acted on — see the module header.
   */
  pollFloorEvent(peerId: string): LatencyFloorEvent | null {
    const peer = this.peers.get(peerId);
    if (!peer || peer.floorMs === null || peer.floorMs <= this.budgetMs || peer.floorEmitted) {
      return null;
    }
    peer.floorEmitted = true;
    return { peerId, atMs: this.nowMs(), floorMs: peer.floorMs, kind: peer.floorKind };
  }

  clearPeer(peerId: string): void {
    this.peers.delete(peerId);
  }

  clearAll(): void {
    this.peers.clear();
  }

  private peerFor(peerId: string): PeerLatency {
    let peer = this.peers.get(peerId);
    if (!peer) {
      peer = {
        samples: [],
        verdict: 'within',
        latestClass: 'unmeasured',
        breachEmitted: false,
        worstLatencyMs: 0,
        floorMs: null,
        floorEmitted: false,
        floorKind: 'unknown',
      };
      this.peers.set(peerId, peer);
    }
    return peer;
  }
}
